import { EmojiPicker } from "@/components/emoji/EmojiPicker.tsx";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { ReactNode, useState } from "react";

export interface EmojiPickerDropdownProps {
  children?: ReactNode;
  onEmojiSelect: (emoji: string) => void;
}

export const EmojiPickerDropdown = ({ children, onEmojiSelect }: EmojiPickerDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), flip(), shift()],
  });

  const dismiss = useDismiss(context, { bubbles: true });
  const click = useClick(context, {
    event: "mousedown",
    toggle: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  return (
    <>
      <div ref={setReference} {...getReferenceProps()}>
        {children}
      </div>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div
              ref={setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              onKeyUp={(event) => {
                event.stopPropagation();
              }}
              className={
                "shadow-md bg-light rounded-sm overflow-hidden overflow-y-auto min-w-52 py-1 flex flex-col px-1 gap-1 max-w-96 z-popover"
              }
            >
              <EmojiPicker
                onEmojiSelect={(emoji) => {
                  onEmojiSelect(emoji);
                  setIsOpen(false);
                }}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
